/**
 * Distillation application grid.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { MathInline, tex } from "@csk/demo-ui";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
export interface DistillationApplicationsProps {
  readonly fixedPoint: boolean;
  readonly initialSurface: number;
  readonly remainingSteps: number;
  readonly surface: number;
}
export function DistillationApplications({
  fixedPoint,
  initialSurface,
  remainingSteps,
  surface,
}: DistillationApplicationsProps): React.JSX.Element {
  const distanceLabel = fixedPoint
    ? "0 distillation steps from full MIT compliance"
    : `${String(remainingSteps)} distillation step${remainingSteps === 1 ? "" : "s"} from full MIT compliance`;
  return (
    <>
      <PanelSurface.SectionHeading>
        What distillation enables
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="mx-1 mt-0 mb-3">
          The same monotone fixed-point distillation that drives the meter above
          is the algorithmic backbone for a family of hardening applications. In
          every case the operator supplies only the authority subgroup{" "}
          <MathInline>{tex`H`}</MathInline> (A1); the violating composites
          become structurally inexpressible by construction, never scanned for
          at runtime. RD-65 catalogs seven; these are the demo-relevant ones.
        </PanelSurface.Prose>
        <PanelSurface.FeatureGrid
          ariaLabel="Applications enabled by vulnerability immunity distillation"
          minColumnWidth={320}
          items={[
            {
              id: "governance-hardening",
              eyebrow: "Compliance Convergence Hardening",
              title: "Automatic governance hardening",
              color: panelTokens.accentGold,
              description: (
                <>
                  Hand VID an arbitrary generator set from{" "}
                  <MathInline>{tex`\mathfrak{gl}(n)`}</MathInline> and it
                  converges to the maximal compliant subset — a provably immune
                  system with no manual axiom checking by the operator.
                </>
              ),
            },
            {
              id: "quantitative-metrics",
              eyebrow: "Quantitative immunity metrics",
              title: "Distance to hardening",
              color: panelTokens.distill,
              description: (
                <>
                  The generator count{" "}
                  <MathInline>{tex`\mu(\mathrm{ALLOW})`}</MathInline> is a
                  concrete measure of how far a system sits from immunity, and
                  the step count is its &ldquo;distance to hardening&rdquo; —
                  this preset reads{" "}
                  <strong className="text-[var(--vid-accent)]">
                    {distanceLabel}
                  </strong>
                  .
                </>
              ),
              meta: `μ ${String(surface)} / ${String(initialSurface)}`,
            },
            {
              id: "differential-analysis",
              eyebrow: "Differential vulnerability analysis",
              title: "Which generators were removed",
              color: panelTokens.violation,
              description: (
                <>
                  Comparing <MathInline>{tex`\mathrm{ALLOW}_{0}`}</MathInline>{" "}
                  with{" "}
                  <MathInline>{tex`\mathrm{IMMUNE}(\mathrm{ALLOW}_{0})`}</MathInline>{" "}
                  names exactly which generators were annihilated — each an A1
                  violation, <MathInline>{tex`\exp(X) \notin H`}</MathInline>.
                  A3 and A4 compliance then follows from GL(n) structure once A1
                  holds.
                </>
              ),
            },
            {
              id: "compositional-hardening",
              eyebrow: "Compositional hardening",
              title: "Independently distilled subsystems compose",
              color: panelTokens.immune,
              description: (
                <>
                  Two subsystems distilled independently stay immune under
                  composition: the monotonicity condition C7 carries over to BCH
                  closure, so when the bracket of two immune generators stays
                  immune the composition is safe by construction.
                </>
              ),
            },
            {
              id: "runtime-audit",
              eyebrow: "Runtime GL(n) audit automation",
              title: "Terminating runtime audit",
              color: panelTokens.accentTeal,
              description: (
                <>
                  In GPU-accelerated settings the audit checks axiom
                  satisfaction for the current generator set and distills any
                  violations each step; the well-founded-descent convergence
                  guarantee means the audit terminates.
                </>
              ),
            },
            {
              id: "ml-safety",
              eyebrow: "Machine learning safety verification",
              title: "Training-time safety filter",
              color: panelTokens.carrier,
              description: (
                <>
                  For weight matrices living in GL(n), distill any
                  axiom-violating weight-matrix generators after each gradient
                  step. The result is a weight matrix satisfying all ten
                  Neutralization Theorems by construction.
                </>
              ),
            },
            {
              id: "spectral-distillation",
              eyebrow: "Spectral distillation",
              title: "Eigenvalue pruning",
              color: panelTokens.accent,
              description: (
                <>
                  Eigenvalue analysis identifies the axiom-violating modes:
                  annihilating a generator corresponds to zeroing out specific
                  spectral modes, connecting distillation to
                  spectral-realization eigenvalue pruning.
                </>
              ),
            },
          ]}
        />
      </PanelSurface.Card>
    </>
  );
}
